/**
 * Drops the comment markers a range does not own end to end.
 *
 * A Word comment marks its extent with three elements: `w:commentRangeStart`,
 * `w:commentRangeEnd`, and a run holding `w:commentReference`. When the
 * comment's highlighted range crosses a paragraph break, a range covering only
 * one of those paragraphs carries a half-range — one boundary marker, with its
 * partner left behind in the paragraph the range does not cover.
 *
 * WHY
 *   `Range.insertOoxml(..., replace)` materializes a comment for every
 *   reference it imports, and it cannot know the reference names a comment the
 *   document already holds. A half-range therefore adds a second copy of a
 *   comment whose surviving partner marker keeps the original alive on the
 *   paragraph the redline never touched — the reviewer sees the same point
 *   raised twice, with reply history on only one copy. Leaving the markers out
 *   leaves the comment to the document, which is where the whole of it lives.
 *
 * SCOPE
 *   Only for a body about to replace a sub-range of a live document. A body
 *   that IS the document owns both ends of every well-formed range, and an
 *   unbalanced marker there is the document's own state to keep, not ours to
 *   delete.
 */
import {
    COMMENT_RANGE_END_TAG,
    COMMENT_RANGE_START_TAG,
    COMMENT_REFERENCE_TAG,
    PROPERTY_TAGS,
    REVISION_CONTAINERS,
    WORDPROCESSINGML_NS,
    elementNameWithoutPrefix,
    wordIdOf,
} from "./ooxml-names";

/** Descendants of `body` with this WordprocessingML local name, read by namespace so a body that binds the namespace to a prefix other than `w:` reads the same. */
function elementsWithLocalName(body: Element, localName: string): Element[] {
    return Array.from(body.getElementsByTagNameNS(WORDPROCESSINGML_NS, localName));
}

/**
 * How many markers of this local name each comment id carries in `body`.
 *
 * WHY a count and not a set
 *   Pairing is what decides ownership. A body holding two `commentRangeEnd`
 *   for one id and a single `commentRangeStart` has an unpaired end, which is
 *   exactly the markup that makes Word mint a duplicate; membership alone
 *   reports that id as owned at both ends and ships the unpaired marker.
 *
 * NULL
 *   A marker carrying no `w:id` names no comment, so it is counted for none
 *   and is never dropped. Bucketing them together under one sentinel id would
 *   let one malformed marker decide the fate of every other id-less one.
 *
 * EXAMPLE
 *   count held for comment id "4" = 2
 *   after one further marker      = 2 + 1 = 3
 */
function markerCountByCommentId(body: Element, localName: string): Map<string, number> {
    const countByCommentId = new Map<string, number>();
    for (const commentRangeMarker of elementsWithLocalName(body, localName)) {
        const commentId = wordIdOf(commentRangeMarker);
        if (commentId === null) continue;
        countByCommentId.set(commentId, (countByCommentId.get(commentId) ?? 0) + 1);
    }
    return countByCommentId;
}

/**
 * The comment ids whose range markers do not pair up inside this body.
 *
 * CASES
 *   - equal counts, both above zero -> the body owns the whole range
 *   - equal counts, both zero       -> a point comment, which has no range to be cut in half
 *   - unequal counts                -> half-anchored: a marker's partner lives outside this body
 *
 * EXAMPLE
 *   startCountByCommentId = {"4": 1}
 *   endCountByCommentId   = {"3": 1, "4": 1}
 *   result                -> {"3"}: comment 3 closes here but opens in the previous paragraph
 */
function halfAnchoredCommentIds(body: Element): Set<string> {
    const startCountByCommentId = markerCountByCommentId(body, COMMENT_RANGE_START_TAG);
    const endCountByCommentId = markerCountByCommentId(body, COMMENT_RANGE_END_TAG);
    const halfAnchoredCommentIdsInBody = new Set<string>();
    for (const commentId of [...startCountByCommentId.keys(), ...endCountByCommentId.keys()]) {
        const startCount = startCountByCommentId.get(commentId) ?? 0;
        const endCount = endCountByCommentId.get(commentId) ?? 0;
        if (startCount !== endCount) halfAnchoredCommentIdsInBody.add(commentId);
    }
    return halfAnchoredCommentIdsInBody;
}

/** Whether every remaining child of the element is a run-properties block, so the element carries no content of its own. */
function holdsOnlyPropertyElements(wordprocessingElement: Element): boolean {
    return Array.from(wordprocessingElement.children).every((childElement) => PROPERTY_TAGS.has(elementNameWithoutPrefix(childElement)));
}

/**
 * Removes the run a dropped reference sat in, and the tracked change that
 * wrapped only that run.
 *
 * CASES
 *   - the run still holds content, such as a kept comment's reference
 *       -> leave it; only the one reference went
 *   - the run held this reference alone
 *       -> remove it, or Word renders a stray empty comment anchor
 *   - removing it empties the `w:ins` / `w:del` around it
 *       -> remove that too, or the review pane shows a tracked change with no content
 *
 * EXAMPLE
 *   run children before = [w:commentReference id=4, w:commentReference id=3]
 *   dropping id 3       -> [w:commentReference id=4], so the run stays
 */
function removeReferenceRunLeftWithoutContent(referenceRun: Element): void {
    if (!holdsOnlyPropertyElements(referenceRun)) return;
    const revisionWrapper = referenceRun.parentElement;
    referenceRun.parentNode?.removeChild(referenceRun);
    if (revisionWrapper === null) return;
    const wrapperIsAnEmptiedTrackedChange =
        REVISION_CONTAINERS.has(elementNameWithoutPrefix(revisionWrapper)) && holdsOnlyPropertyElements(revisionWrapper);
    if (wrapperIsAnEmptiedTrackedChange) revisionWrapper.parentNode?.removeChild(revisionWrapper);
}

/** Strips every comment marker in `body` whose comment the body anchors at one end only. */
export function dropHalfAnchoredCommentMarkers(body: Element): void {
    const halfAnchoredIds = halfAnchoredCommentIds(body);
    for (const markerTag of [COMMENT_RANGE_START_TAG, COMMENT_RANGE_END_TAG]) {
        for (const rangeMarker of elementsWithLocalName(body, markerTag)) {
            const commentId = wordIdOf(rangeMarker);
            if (commentId === null || !halfAnchoredIds.has(commentId)) continue;
            rangeMarker.parentNode?.removeChild(rangeMarker);
        }
    }
    for (const referenceElement of elementsWithLocalName(body, COMMENT_REFERENCE_TAG)) {
        const commentId = wordIdOf(referenceElement);
        if (commentId === null || !halfAnchoredIds.has(commentId)) continue;
        const referenceRun = referenceElement.parentElement;
        referenceElement.parentNode?.removeChild(referenceElement);
        if (referenceRun !== null) removeReferenceRunLeftWithoutContent(referenceRun);
    }
}
